import { firstValueFrom, fromEvent, merge } from 'rxjs'
import { take } from 'rxjs/operators'
import { shareLatest } from '@/utils/rx-util'
import type { Storage } from '@/infra/storage'
import { action, observable } from 'mobx'
import { toStream } from '@/utils/mobx-util'

// Sounds
import soundSourceGotMessageMp3 from '@/assets/sounds/got-message.mp3'
import soundSourceGotMessageOgg from '@/assets/sounds/got-message.ogg'
import soundSourceSendMessageMp3 from '@/assets/sounds/send-message.mp3'
import soundSourceSendMessageOgg from '@/assets/sounds/send-message.ogg'
import { onAbort, resolveOnAbort } from '@/utils/abort-util'

/**
 * The available sounds.
 */
export type Sound = 'GotMessage' | 'MessageSent'

/**
 * Map of sounds and where to load them.
 */
const sounds = new Map<Sound, SoundSource>([
  [
    'GotMessage',
    {
      mp3: soundSourceGotMessageMp3,
      ogg: soundSourceGotMessageOgg,
    },
  ],
  [
    'MessageSent',
    {
      mp3: soundSourceSendMessageMp3,
      ogg: soundSourceSendMessageOgg,
    },
  ],
])

interface SoundSource {
  mp3: string
  ogg: string
}

/**
 * The sound player.
 */
export class SoundPlayer {
  /**
   * We can only play sound once we have interacted with the document at least once.
   * We emit the first interaction.
   * @private
   */
  private readonly _ready$ = merge(
    fromEvent(window, 'mousedown', { capture: true }),
    fromEvent(window, 'click', { capture: true }),
    fromEvent(window, 'keypress', { capture: true }),
  ).pipe(
    // Unsubscribe after the first interaction.
    take(1),
    // Once we have the first one, emit that on subscribing.
    shareLatest(),
  )

  /**
   * Whether the sound player is enabled.
   */
  @observable accessor isEnabled = false

  constructor(private readonly _storage: Storage) {
    this.playSound = this.playSound.bind(this)
    this._ready$.subscribe()
    this.setEnabled(_storage.getItem('soundEnabled') !== 'disabled')
    toStream(() => this.isEnabled).subscribe((enabled) =>
      this._storage.setItem('soundEnabled', enabled ? 'enabled' : 'disabled'),
    )
  }

  /**
   * Toggles whether the sound player is enabled.
   *
   * @param enabled
   */
  @action.bound
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled
  }

  /**
   * Plays the specified sound.
   *
   * @param sound
   * @param signal
   */
  async playSound(sound: Sound, signal?: AbortSignal) {
    if (!this.isEnabled) {
      return
    }

    const audio = SoundPlayer._getAudio(sound)
    await resolveOnAbort(
      firstValueFrom(this._ready$, { defaultValue: null }),
      signal,
    )
    if (signal?.aborted) {
      return
    }

    const dispose = onAbort(signal, () => {
      audio.pause()
    })

    try {
      audio.currentTime = 0
      await audio.play()
    } catch (err: unknown) {
      console.warn(`Unable to play sound ${sound}`, err)
    } finally {
      dispose()
    }
  }

  /**
   * Gets the audio object for the specified sound.
   *
   * @param sound
   */
  private static _getAudio(sound: Sound) {
    const audio = new window.Audio()
    const type = audio.canPlayType('audio/mpeg') ? 'mp3' : 'ogg'
    audio.volume = 0.6
    audio.src = sounds.get(sound)![type]
    audio.pause()
    return audio
  }
}
